import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { Box } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IIntegrationRuleFields } from "../../../../../../models/integration-rule-field";
import MainApi from "../../../../../../services/mainApi.service";
import { IntegrationRuleField } from "./IntegrationRuleFieldForm/IntegrationRuleField";

const INTEGRATION_RULE_FIELD_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/4uBusIntegrationRuleField/";
const integrationRuleFieldService = new MainApi<IIntegrationRuleFields>(
  INTEGRATION_RULE_FIELD_ROUTE,
);

const translationPath = "page.4uBus.integrationRule.integrationRuleField.";

export const IntegrationRuleFieldPage = memo(() => {
  const { id: busIntegrationRuleId } = useParams();

  const { t } = useTranslation();

  const [fieldForm, setFieldForm] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<IIntegrationRuleFields>[] = [
    {
      field: "automationField",
      headerName: t(translationPath + "automation_field") || "Automation Field",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "individualExternalId",
      headerName:
        t(translationPath + "individual_external_id") ||
        "Individual External ID",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.toString()}
            tooltip={params.value.toString()}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "_4uBusIntegrationRuleFieldSoftwares",
      headerName: t(translationPath + "softwares") || "Softwares",
      flex: 1,
      renderCell: (params) => {
        const softwares = params.row._4uBusIntegrationRuleFieldSoftwares
          ?.map((item) => item.software?.name)
          .filter(Boolean)
          .join(", ");

        if (!softwares) return "-";
        return (
          <OverflowTooltip
            text={softwares}
            tooltip={softwares}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    setFieldForm(true);
  };

  const handleRowClick = (params: GridRowParams<IIntegrationRuleFields>) => {
    if (!params.row) return;
    setFieldForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    setFieldForm(false);
  };

  const params = useMemo(() => {
    return {
      _4uBusIntegrationRuleId: busIntegrationRuleId,
    };
  }, [busIntegrationRuleId]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {fieldForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <IntegrationRuleField
            id={selectedId}
            busIntegrationRuleId={busIntegrationRuleId}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!fieldForm && (
        <Box height={600}>
          <Table<IIntegrationRuleFields>
            showDefaultMacroFilters={false}
            service={integrationRuleFieldService}
            serviceProps={params}
            columns={columns}
            title={t(translationPath + "integration_rule_fields")}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputPlaceHolder={t(translationPath + "search")}
            addButtonLabel={
              t(translationPath + "add_integration_rule_field") ||
              "Add integration rule field"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
});
